import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type {
  User,
  Tenant,
  Account,
  AccountRequest,
  Category,
  CategoryRequest,
  Tag,
  TagRequest,
  Transaction,
  TransactionRequest,
  SignUpRequest,
  TenantUpdateRequest,
  UserCreateRequest,
  UserUpdateRequest,
  Error as APIError,
  CategoryResponse,
  CurrencyRate,
  PortfolioItem,
  ManualAssetValue,
  PortfolioHolding,
  PortfolioValueHistory,
  DebtTerms,
  DebtTermsRequest,
  AnalyticsResponse,
  TagAnalyticsResponse,
  PlaidItem,
  PlaidTransaction,
  PlaidTransactionsResponse,
  ImportAdapter,
  DetectAdapterResult,
  StagedImportResponse,
  StagedImportRow,
  CreateAdapterRequest,
  PlaidSyncLog,
  MerchantHistoryTransaction,
  SeedItem,
  Insight,
  UserSignal,
  RebuildStatusResponse,
  RebuildTriggerRequest,
  RebuildTriggerResponse,
  SubscriptionsResponse,
  SubscriptionsView,
  RecurringCadence,
  EnableBankingAspspsResponse,
  EnableBankingCredentialsRequest,
  EnableBankingCredentialsView,
  EnableBankingStatus,
  EnableBankingConnectRequest,
  EnableBankingConnectResponse,
  EnableBankingConnectionResponse,
  EnableBankingMapRequest,
  EnableBankingMapResponse,
  EtoroConnection,
  EtoroConnectRequest,
  EtoroConnectResponse,
  EtoroUpdateRequest,
  ConvertToCopyTradingResponse,
} from '../types/api';
import type {
  AssetIncomeTermsResponse,
  IncomeTerms,
  IncomeTermsRequest,
  IncomeStream,
  IncomeStreamsResponse,
  DetachedIncomeTerms,
  PassiveIncomeResponse,
} from '../types/passive-income';
import type {
  Integration,
  ApiKeySummary,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  CreateIntegrationRequest,
  CreateIntegrationResponse,
  IntegrationAccessLevel,
  KeyExpiryDays,
} from '../types/integrations';
import type { OAuthConsentResponse, OAuthDecisionResponse } from '../types/oauth';
import type { ActivityResponse } from '../types/activity';

export interface AggregatedPortfolioHistory {
  date: string;
  totalUSD: number;
  totalPortfolioCurrency?: number | null;
  Investments?: {
    total: number;
    groups: { [key: string]: number };
  };
  Asset?: {
    total: number;
    groups: { [key: string]: number };
  };
  Debt?: {
    total: number;
    groups: { [key: string]: number };
  };
}

// Regular expression to check for strings that look like numbers (including decimals and negatives)
const numericRegex = /^-?\d+(\.\d+)?$/;

/**
 * Recursively walks through an object or array and parses numeric strings into numbers.
 * @param data The data to parse (object, array, or primitive).
 * @returns The parsed data.
 */
function recursiveNumericParser(data: unknown): unknown {
  if (data === null || data === undefined) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map(recursiveNumericParser);
  }

  if (typeof data === 'object') {
    return Object.entries(data as Record<string, unknown>).reduce((acc, [key, value]) => {
      acc[key] = recursiveNumericParser(value);
      return acc;
    }, {} as Record<string, unknown>);
  }

  if (typeof data === 'string' && numericRegex.test(data)) {
    // Check if parsing would result in a very large number that loses precision
    const num = parseFloat(data);
    if (Math.abs(num) > Number.MAX_SAFE_INTEGER) {
      return data; // Keep as string if it's too large to be a safe integer
    }
    return num;
  }

  return data;
}

interface SignInRequest {
  email: string;
  password: string;
}

interface Session {
  user: User;
}

interface Country {
  id: string;
  name: string;
  emoji?: string;
}

interface Currency {
  id: string;
  name: string;
  symbol?: string;
}

interface Bank {
  id: number;
  name: string;
}

interface PlaidAccountDetail {
  accountId: string;
  name: string;
  mask: string;
  type: string;
  subtype: string;
  currentBalance: number;
  isoCurrencyCode: string;
  isCurrencySupported: boolean;
}

interface PlaidAccountsResponse {
  accounts: PlaidAccountDetail[];
  institution: string;
  tenantId?: string;
  institutionCountry?: string | null;
  unsupportedCountry?: string | null;
  unsupportedCountryId?: string | null;
  unsupportedCurrencies?: string[];
  tenantCurrencies?: string[];
  tenantCountries?: Array<{ id: string; iso2: string | null }>;
  earliestDate?: string | null;
}

type OnboardingProgress = Record<string, boolean>;

/**
 * Plain JSON parsing without the numeric-string coercion of the default
 * transform. Enable Banking responses carry IBAN last-4 digits ("0123") and
 * decimal balances as strings that must stay strings.
 */
const rawJsonResponse = [(data: unknown) => {
  try {
    return typeof data === 'string' && data.length > 0 ? JSON.parse(data) : data;
  } catch {
    return data;
  }
}];

// Fire session-expired at most once until the next successful sign-in.
let sessionExpiredFired = false;

/** Re-arm the session-expired event after a successful sign-in (#156). */
export function resetSessionExpired(): void {
  sessionExpiredFired = false;
}

/**
 * Auth routes whose 401 means "wrong credentials" or "no session", never "the
 * access token expired": they never trigger a refresh, and never fire
 * `auth:session-expired` (a wrong password in the confirm-identity dialog must
 * not sign the user out, #156 R20).
 */
const NO_REFRESH_URLS = new Set([
  '/api/auth/refresh',
  '/api/auth/signin',
  '/api/auth/signup',
  '/api/auth/signout',
  '/api/auth/reauth',
]);

type RetryableConfig = InternalAxiosRequestConfig & { _retried?: boolean; _sentAt?: number };

/** `403 REAUTH_REQUIRED`: the action needs a sign-in within 15 minutes (#156). */
export function isReauthRequired(error: unknown): boolean {
  const e = error as AxiosError<{ code?: string }>;
  return e?.response?.status === 403 && e.response.data?.code === 'REAUTH_REQUIRED';
}

class APIClient {
  private client: AxiosInstance;
  /**
   * Bare instance for POST /api/auth/refresh: no interceptors, so the refresh
   * call can never re-enter the 401 handler (#156 R24).
   */
  private refreshClient: AxiosInstance;
  private refreshPromise: Promise<boolean> | null = null;
  private lastRefreshAt = 0;

  constructor() {
    const baseURL = (import.meta.env.NEXT_PUBLIC_API_URL || '').replace(/\/$/, '');

    // Remove the /api suffix if it exists in the base URL
    const normalizedBaseURL = baseURL.endsWith('/api')
      ? baseURL.slice(0, -4)
      : baseURL;

    this.client = axios.create({
      baseURL: normalizedBaseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true, // Important for cross-domain cookies
      transformResponse: [(data) => {
        try {
          // Axios automatically parses JSON, but if it fails and returns a string, parse it.
          const jsonData = typeof data === 'string' ? JSON.parse(data) : data;
          return recursiveNumericParser(jsonData);
        } catch (error) {
          return data; // Return original data if parsing fails
        }
      }],
    });

    this.refreshClient = axios.create({
      baseURL: normalizedBaseURL,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });

    // Remember when each request left, so a 401 for a request sent before
    // the latest successful refresh just retries instead of refreshing again.
    this.client.interceptors.request.use((config) => {
      (config as RetryableConfig)._sentAt = Date.now();
      return config;
    });

    // Response interceptor for handling errors
    this.client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error: AxiosError<APIError>) => {
        const config = error.config as RetryableConfig | undefined;
        const url = config?.url ?? '';
        const status = error.response?.status;
        const isSessionCheck = url === '/api/auth/session';
        const isAuthCredentialCall = NO_REFRESH_URLS.has(url);

        // Access token expired (15 min, #156): refresh once, then retry the
        // request once. Concurrent 401s (e.g. the resume refetch burst) share
        // one in-flight refresh. The session check refreshes too, so an
        // installed app opened days later lands signed in.
        if (status === 401 && config && !config._retried && !isAuthCredentialCall) {
          const refreshed = this.lastRefreshAt > (config._sentAt ?? 0) || await this.refreshOnce();
          if (refreshed) {
            config._retried = true;
            return this.client.request(config);
          }
        }

        // A 401 on the session check is expected when the user is not logged in — don't log it.
        if (!isSessionCheck) {
          console.error(`[API Error] ${status} ${error.config?.method?.toUpperCase()} ${url}`, error.response?.data);
        }
        // The refresh failed: emit a custom event so AuthContext can clear the
        // user and show a toast. Do NOT redirect here — window.location.href
        // causes a full page reload which remounts AuthContext → infinite loop.
        // The withAuth HOC handles the redirect. The session check stays
        // silent — a 401 there just means the user isn't logged in.
        if (status === 401 && !isSessionCheck && !isAuthCredentialCall && !sessionExpiredFired) {
          sessionExpiredFired = true;
          window.dispatchEvent(new Event('auth:session-expired'));
        }
        return Promise.reject(error);
      }
    );
  }

  /**
   * One refresh at a time per tab, and — via the Web Locks API where
   * available — across tabs, so a second tab refreshes with the cookie the
   * first one already rotated. The server's 10 s grace window covers browsers
   * without Web Locks.
   */
  private refreshOnce(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;
    const run = async (): Promise<boolean> => {
      try {
        await this.refreshClient.post('/api/auth/refresh');
        this.lastRefreshAt = Date.now();
        return true;
      } catch {
        return false;
      }
    };
    const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
    const attempt = locks?.request
      ? (async () => locks.request('bliss-auth-refresh', run))()
      : run();
    this.refreshPromise = attempt.finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  // Auth
  async signup(data: SignUpRequest): Promise<{ user: User; tenant: Tenant }> {
    const response = await this.client.post('/api/auth/signup', data);
    return response.data;
  }

  /** Public sign-up mode flag (#99). Only ever `{ inviteOnly }`. */
  async getSignupMode(): Promise<{ inviteOnly: boolean }> {
    const response = await this.client.get('/api/auth/signup-mode');
    return { inviteOnly: response.data?.inviteOnly === true };
  }

  async signin(data: SignInRequest): Promise<{ user: User }> {
    const response = await this.client.post('/api/auth/signin', data);
    return response.data;
  }

  async signout(): Promise<void> {
    await this.client.post('/api/auth/signout');
  }

  async getSession(): Promise<Session | null> {
    const response = await this.client.get('/api/auth/session');
    return response.data;
  }

  async getScriptToken(): Promise<{ token: string; expiresIn: number }> {
    const response = await this.client.post('/api/auth/get-script-token');
    return response.data;
  }

  async changePassword(data: { currentPassword: string; newPassword: string; confirmPassword: string }): Promise<{ message: string }> {
    const response = await this.client.put('/api/auth/change-password', data);
    return response.data;
  }

  /** Recent sign-in status and the ways this user can confirm it (#156). */
  async getReauthStatus(): Promise<{ recent: boolean; methods: ('password' | 'google')[] }> {
    const response = await this.client.get('/api/auth/reauth');
    return response.data;
  }

  /** Confirm identity with the password; rejects with 401 REAUTH_FAILED (#156). */
  async reauthWithPassword(password: string): Promise<void> {
    await this.client.post('/api/auth/reauth', { password });
  }

  /** "Sign out of all other devices" (#156). This device stays signed in. */
  async signOutOtherDevices(): Promise<{ revoked: number }> {
    const response = await this.client.post('/api/auth/sessions/revoke-others');
    return response.data;
  }

  // Tenants
  async getTenants(params?: { name?: string; plan?: 'FREE' | 'PRO' | 'AI' }): Promise<Tenant[]> {
    const response = await this.client.get('/api/tenants', { params });
    return response.data;
  }

  async getTenant(id: string): Promise<Tenant> {
    const response = await this.client.get('/api/tenants', { params: { id } });
    return response.data;
  }

  async updateTenant(id: string, data: TenantUpdateRequest): Promise<Tenant> {
    const response = await this.client.put('/api/tenants', data, { params: { id } });
    return response.data;
  }

  async deleteTenant(id: string): Promise<void> {
    await this.client.delete('/api/tenants', { params: { id } });
  }

  // Users
  async getUsers(params?: { email?: string }): Promise<User[]> {
    const response = await this.client.get('/api/users', { params });
    return response.data;
  }

  async createUser(data: UserCreateRequest): Promise<User> {
    const response = await this.client.post('/api/users', data);
    return response.data;
  }

  async updateUser(id: string, data: UserUpdateRequest): Promise<User> {
    const response = await this.client.put('/api/users', data, { params: { id } });
    return response.data;
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete('/api/users', { params: { id } });
  }

  // Accounts
  async getAccounts(params?: {
    id?: number;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    name?: string;
    bankId?: number;
    countryId?: string;
    currencyCode?: string;
    ownerId?: string;
  }): Promise<{ accounts: Account[]; total: number; page: number; limit: number; totalPages: number }> {
    const response = await this.client.get('/api/accounts', { params });
    return response.data;
  }

  async createAccount(data: AccountRequest): Promise<Account> {
    const response = await this.client.post('/api/accounts', data);
    return response.data;
  }

  async updateAccount(id: number, data: Partial<AccountRequest>): Promise<Account> {
    const response = await this.client.put('/api/accounts', data, { params: { id } });
    return response.data;
  }

  async deleteAccount(id: number): Promise<void> {
    await this.client.delete('/api/accounts', { params: { id } });
  }

  // Categories
  async getCategories(params?: {
    name?: string;
    group?: string;
    type?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }): Promise<CategoryResponse> {
    const response = await this.client.get('/api/categories', { params });
    return response.data;
  }

  async createCategory(data: CategoryRequest): Promise<Category> {
    const response = await this.client.post('/api/categories', data);
    return response.data;
  }

  async updateCategory(id: number, data: CategoryRequest): Promise<Category> {
    const response = await this.client.put('/api/categories', data, { params: { id } });
    return response.data;
  }

  async deleteCategory(id: number, mergeInto?: number): Promise<void> {
    await this.client.delete('/api/categories', { params: { id, ...(mergeInto && { mergeInto }) } });
  }

  // Tags
  async getTags(params?: { name?: string }): Promise<Tag[]> {
    const response = await this.client.get('/api/tags', { params });
    return response.data;
  }

  async createTag(data: TagRequest): Promise<Tag> {
    const response = await this.client.post('/api/tags', data);
    return response.data;
  }

  async updateTag(id: number, data: Partial<TagRequest>): Promise<Tag> {
    const response = await this.client.put('/api/tags', data, { params: { id } });
    return response.data;
  }

  async deleteTag(id: number): Promise<void> {
    await this.client.delete('/ tags', { params: { id } });
  }

  // Transactions
  async getTransactions(params?: {
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    accountId?: number;
    categoryId?: number;
    /** Comma-separated Transaction ids — used by the Subscriptions expand-row view. */
    ids?: string;
    tagIds?: string;
    startDate?: string;
    endDate?: string;
    year?: number;
    month?: number;
    quarter?: string;
    group?: string;
    type?: 'Income' | 'Expense' | 'Transfer' | 'Investment';
    source?: string;
    currencyCode?: string;
  }): Promise<{ transactions: Transaction[]; total: number; page: number; limit: number; totalPages: number }> {
    const response = await this.client.get('/api/transactions', { params });
    return response.data;
  }

  async createTransaction(data: TransactionRequest): Promise<Transaction> {
    const response = await this.client.post('/api/transactions', data);
    return response.data;
  }

  async updateTransaction(id: number, data: Partial<TransactionRequest>): Promise<Transaction> {
    const response = await this.client.put('/api/transactions', data, { params: { id } });
    return response.data;
  }

  async deleteTransaction(id: number): Promise<void> {
    await this.client.delete('/api/transactions', { params: { id } });
  }

  async exportTransactions(params?: {
    startDate?: string;
    endDate?: string;
    accountId?: number;
    categoryId?: number;
    group?: string;
    type?: string;
    source?: string;
    currencyCode?: string;
    tags?: string[];
  }): Promise<Blob> {
    const response = await this.client.get('/api/transactions/export', {
      params,
      responseType: 'blob',
      transformResponse: [(data: unknown) => data], // skip JSON parsing for blob
    });
    return response.data;
  }

  // Currency Rates
  async getCurrencyRates(params?: {
    id?: number;
    year?: number;
    month?: number;
    currencyFrom?: string;
    currencyTo?: string;
  }): Promise<CurrencyRate[]> {
    const response = await this.client.get('/api/currency-rates', { params });
    return response.data;
  }

  async createOrUpdateCurrencyRate(data: {
    year: number;
    month: number;
    day: number;
    currencyFrom: string;
    currencyTo: string;
    value: number | string;
    provider?: string;
  }): Promise<CurrencyRate> {
    const response = await this.client.post('/api/currency-rates', data);
    return response.data;
  }

  async updateCurrencyRate(id: number, data: {
    year: number;
    month: number;
    day: number;
    currencyFrom: string;
    currencyTo: string;
    value: number | string;
    provider?: string;
  }): Promise<CurrencyRate> {
    const response = await this.client.put('/api/currency-rates', data, { params: { id } });
    return response.data;
  }

  async deleteCurrencyRate(id: number): Promise<void> {
    await this.client.delete('/api/currency-rates', { params: { id } });
  }

  // Asset Prices
  async getAssetPrice(params: {
    symbol?: string;
    symbols?: string;
    type: 'Equity' | 'Crypto';
    currency?: string;
    date?: string;
    forceRefresh?: boolean;
    cacheDuration?: number;
  }): Promise<Record<string, unknown>> {
    const response = await this.client.get('/api/asset-price', { params });
    return response.data;
  }

  // Metadata endpoints
  async getCurrencies(): Promise<Currency[]> {
    const response = await this.client.get('/api/currencies');
    return response.data;
  }

  async getCountries(): Promise<Country[]> {
    const response = await this.client.get('/api/countries');
    return response.data;
  }

  async getBanks(): Promise<Bank[]> {
    const response = await this.client.get('/api/banks');
    return response.data;
  }

  async getUserPreferences() {
    const response = await this.client.get('/api/user/preferences');
    return response.data;
  }

  async getAnalytics(params: {
    view: 'year' | 'quarter' | 'month';
    years?: number[];
    startMonth?: string;
    endMonth?: string;
    startQuarter?: string;
    endQuarter?: string;
    currency?: string;
    countries?: string[];
    types?: string[];
    groups?: string[];
  }): Promise<AnalyticsResponse> {
    // Convert array parameters to form style
    const formParams = new URLSearchParams();

    // Add non-array parameters
    if (params.view) formParams.append('view', params.view);
    if (params.currency) formParams.append('currency', params.currency);
    if (params.startMonth) formParams.append('startMonth', params.startMonth);
    if (params.endMonth) formParams.append('endMonth', params.endMonth);
    if (params.startQuarter) formParams.append('startQuarter', params.startQuarter);
    if (params.endQuarter) formParams.append('endQuarter', params.endQuarter);

    // Add array parameters with explode: true
    if (params.years) {
      params.years.forEach(year => formParams.append('years', year.toString()));
    }
    if (params.countries) {
      params.countries.forEach(country => formParams.append('countries', country));
    }
    if (params.types) {
      params.types.forEach(type => formParams.append('types', type));
    }
    if (params.groups) {
      params.groups.forEach(group => formParams.append('groups', group));
    }

    const response = await this.client.get('/api/analytics', {
      params: formParams,
      paramsSerializer: params => params.toString() // Ensure proper form serialization
    });
    return response.data;
  }

  async getTagAnalytics(params: {
    tagIds: number[];
    view: 'year' | 'quarter' | 'month';
    years?: number[];
    startMonth?: string;
    endMonth?: string;
    startQuarter?: string;
    endQuarter?: string;
    currency?: string;
  }): Promise<TagAnalyticsResponse> {
    const formParams = new URLSearchParams();
    if (params.view) formParams.append('view', params.view);
    if (params.currency) formParams.append('currency', params.currency);
    if (params.startMonth) formParams.append('startMonth', params.startMonth);
    if (params.endMonth) formParams.append('endMonth', params.endMonth);
    if (params.startQuarter) formParams.append('startQuarter', params.startQuarter);
    if (params.endQuarter) formParams.append('endQuarter', params.endQuarter);
    params.tagIds.forEach(id => formParams.append('tagIds[]', id.toString()));
    if (params.years) {
      params.years.forEach(year => formParams.append('years', year.toString()));
    }
    const response = await this.client.get('/api/analytics/tags', {
      params: formParams,
      paramsSerializer: params => params.toString()
    });
    return response.data;
  }

  // --- Portfolio ---

  async getPortfolioItems(filters: {
    assetType?: string;
    source?: string;
    include_manual_values?: boolean;
    /** Filter by brokerage account ID */
    accountId?: number | null;
    /** Filter by account country (ISO 3166-1 alpha-3, e.g. "USA") */
    countryId?: string;
  } = {}): Promise<{ portfolioCurrency: string; items: PortfolioItem[] }> {
    const response = await this.client.get('/api/portfolio/items', { params: filters });
    return response.data;
  }

  async getPortfolioHoldings(filters: {
    /** Filter by brokerage account ID */
    account?: number;
    /** Filter by account country (ISO 3166-1 alpha-3) */
    countryId?: string;
    category?: string;
    categoryGroup?: string;
    ticker?: string;
  } = {}): Promise<PortfolioHolding[]> {
    const response = await this.client.get('/api/portfolio/holdings', { params: filters });
    return response.data;
  }

  async getPortfolioHistory(
    filters: {
      from?: string;
      to?: string;
      type?: string;
      group?: string;
      /** Filter history to a specific brokerage account */
      accountId?: number;
      /** Only this holding: every item with this symbol (#131). Exclusive with itemId. */
      symbol?: string;
      /** Only this portfolio item — manual / symbol-less assets (#131). */
      itemId?: number;
      resolution?: 'daily' | 'weekly' | 'monthly';
    } = {}
  ): Promise<{ portfolioCurrency: string; resolution: 'daily' | 'weekly' | 'monthly'; history: AggregatedPortfolioHistory[] }> {
    const response = await this.client.get('/api/portfolio/history', { params: filters });
    return response.data;
  }

  // --- Manual Values and Debt Terms (nested under a portfolio item) ---

  async getManualAssetValues(itemId: number): Promise<ManualAssetValue[]> {
    const response = await this.client.get(`/api/portfolio/items/${itemId}/manual-values`);
    return response.data;
  }

  async createManualAssetValue(
    itemId: number,
    data: {
      date: string;
      value: number;
      currency: string;
      notes?: string;
    }
  ): Promise<ManualAssetValue> {
    const response = await this.client.post(`/api/portfolio/items/${itemId}/manual-values`, data);
    return response.data;
  }

  async updateManualAssetValue(
    itemId: number,
    valueId: string,
    data: Partial<{ date: string; value: number; currency: string; notes?: string; }>
  ): Promise<ManualAssetValue> {
    const response = await this.client.put(
      `/api/portfolio/items/${itemId}/manual-values/${valueId}`,
      data
    );
    return response.data;
  }

  async deleteManualAssetValue(itemId: number, valueId: string): Promise<void> {
    await this.client.delete(`/api/portfolio/items/${itemId}/manual-values/${valueId}`);
  }

  /** Debt terms for one item, or null when none have been entered (API 404). */
  async getDebtTerms(itemId: number): Promise<DebtTerms | null> {
    try {
      const response = await this.client.get(`/api/portfolio/items/${itemId}/debt-terms`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    }
  }

  // --- Manage Assets (#81) ---

  async getManageAssets(
    params: import('@/types/manage-assets').ManageAssetsFilters & { id?: number; cursor?: string | null; limit?: number } = {},
  ): Promise<import('@/types/manage-assets').ManageAssetsResponse> {
    const query: Record<string, string | number> = {};
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '' || value === false) continue;
      query[key] = typeof value === 'boolean' ? String(value) : (value as string | number);
    }
    const response = await this.client.get('/api/portfolio/assets', { params: query });
    return response.data;
  }

  async getAssetClass(portfolioItemId: number): Promise<import('@/types/manage-assets').AssetClassInfo> {
    const response = await this.client.get(`/api/portfolio/items/${portfolioItemId}/asset-class`);
    return response.data;
  }

  async createOrUpdateDebtTerms(itemId: number, data: DebtTermsRequest): Promise<DebtTerms> {
    const response = await this.client.post(`/api/portfolio/items/${itemId}/debt-terms`, data);
    return response.data;
  }

  // --- Plaid ---

  async createBank(data: { name: string }): Promise<Bank> {
    const response = await this.client.post('/api/banks', data);
    return response.data;
  }

  async createLinkToken(plaidItemId?: string): Promise<{ link_token: string; expiration: string; request_id: string }> {
    const response = await this.client.post('/api/plaid/create-link-token', plaidItemId ? { plaidItemId } : {});
    return response.data;
  }

  async exchangePublicToken(public_token: string, metadata: Record<string, unknown>, bankName?: string): Promise<{ plaidItemId: string }> {
    const institution = metadata.institution as { institution_id?: string; name?: string } | undefined;
    const response = await this.client.post('/api/plaid/exchange-public-token', {
      public_token,
      institutionId: institution?.institution_id,
      institutionName: institution?.name,
      bankName: bankName, // Pass custom bank name if provided
    });
    return response.data;
  }

  async getPlaidAccounts(plaidItemId: string): Promise<PlaidAccountsResponse> {
    const response = await this.client.get('/api/plaid/accounts', { params: { plaidItemId } });
    return response.data;
  }

  async syncPlaidAccounts(plaidItemId: string, selectedAccountIds: string[], countryId?: string, accountMappings?: Record<string, number>, accountNames?: Record<string, string>): Promise<void> {
    await this.client.post('/api/plaid/sync-accounts', { plaidItemId, selectedAccountIds, countryId, accountMappings, accountNames });
  }

  async getPlaidItems(): Promise<PlaidItem[]> {
    const response = await this.client.get('/api/plaid/items');
    return response.data;
  }

  async updatePlaidItem(id: string, data: { status?: string }): Promise<PlaidItem> {
    const response = await this.client.patch('/api/plaid/items', data, { params: { id } });
    return response.data;
  }

  // --- Enable Banking (#132) ---

  /** Whether the tenant has Enable Banking credentials (#148). When false, no Enable Banking UI renders. */
  async getEnableBankingStatus(): Promise<EnableBankingStatus> {
    const response = await this.client.get('/api/enablebanking/status');
    return {
      enabled: response.data?.enabled === true,
      configurable: response.data?.configurable === true,
      pausedConnections: Number(response.data?.pausedConnections) || 0,
    };
  }

  /** The tenant's Enable Banking credentials (admins only, #148). Never contains the private key. */
  async getEnableBankingCredentials(): Promise<EnableBankingCredentialsView> {
    const response = await this.client.get('/api/enablebanking/credentials', { transformResponse: rawJsonResponse });
    return response.data;
  }

  async saveEnableBankingCredentials(data: EnableBankingCredentialsRequest): Promise<EnableBankingCredentialsView> {
    const response = await this.client.put('/api/enablebanking/credentials', data, { transformResponse: rawJsonResponse });
    return response.data;
  }

  async removeEnableBankingCredentials(confirm: boolean): Promise<void> {
    await this.client.delete('/api/enablebanking/credentials', { params: confirm ? { confirm: 'true' } : undefined });
  }

  async verifyEnableBankingCredentials(): Promise<EnableBankingCredentialsView> {
    const response = await this.client.post('/api/enablebanking/credentials/verify', null, { transformResponse: rawJsonResponse });
    return response.data;
  }

  async getEnableBankingAspsps(): Promise<EnableBankingAspspsResponse> {
    const response = await this.client.get('/api/enablebanking/aspsps', { transformResponse: rawJsonResponse });
    return response.data;
  }

  async connectEnableBanking(data: EnableBankingConnectRequest): Promise<EnableBankingConnectResponse> {
    const response = await this.client.post('/api/enablebanking/connect', data, { transformResponse: rawJsonResponse });
    return response.data;
  }

  async getEnableBankingConnection(id: string): Promise<EnableBankingConnectionResponse> {
    const response = await this.client.get(`/api/enablebanking/connections/${encodeURIComponent(id)}`, {
      transformResponse: rawJsonResponse,
    });
    return response.data;
  }

  async mapEnableBankingConnection(id: string, data: EnableBankingMapRequest): Promise<EnableBankingMapResponse> {
    const response = await this.client.post(`/api/enablebanking/connections/${encodeURIComponent(id)}/map`, data, {
      transformResponse: rawJsonResponse,
    });
    return response.data;
  }

  async syncEnableBankingConnection(id: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/enablebanking/connections/${encodeURIComponent(id)}/sync`, null, {
      transformResponse: rawJsonResponse,
    });
    return response.data;
  }

  async disconnectEnableBankingConnection(id: string): Promise<void> {
    await this.client.delete(`/api/enablebanking/connections/${encodeURIComponent(id)}`);
  }

  // --- eToro copy trading (#143) ---

  async getEtoroConnections(): Promise<EtoroConnection[]> {
    const response = await this.client.get('/api/etoro/connections', { transformResponse: rawJsonResponse });
    return response.data?.connections ?? [];
  }

  /** Connect an eToro account. The keys travel in this request only and are never returned. */
  async connectEtoro(data: EtoroConnectRequest): Promise<EtoroConnectResponse> {
    const response = await this.client.post('/api/etoro/connections', data, { transformResponse: rawJsonResponse });
    return response.data;
  }

  async updateEtoroConnection(id: string, data: EtoroUpdateRequest): Promise<EtoroConnection> {
    const response = await this.client.put(`/api/etoro/connections/${encodeURIComponent(id)}`, data, {
      transformResponse: rawJsonResponse,
    });
    return response.data.connection;
  }

  async removeEtoroConnection(id: string): Promise<void> {
    await this.client.delete(`/api/etoro/connections/${encodeURIComponent(id)}`);
  }

  async syncEtoroConnection(id: string): Promise<{ message: string }> {
    const response = await this.client.post(`/api/etoro/connections/${encodeURIComponent(id)}/sync`, null, {
      transformResponse: rawJsonResponse,
    });
    return response.data;
  }

  /** Switch a holding to Copy Trading in place (#143, R8). */
  async convertToCopyTrading(portfolioItemId: number, etoroUsername: string): Promise<ConvertToCopyTradingResponse> {
    const response = await this.client.post('/api/etoro/convert-holding', { portfolioItemId, etoroUsername }, {
      transformResponse: rawJsonResponse,
    });
    return response.data;
  }

  // --- Smart Import ---

  async detectAdapter(file: File): Promise<DetectAdapterResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post('/api/imports/detect-adapter', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getAdapters(): Promise<ImportAdapter[]> {
    const response = await this.client.get('/api/imports/adapters');
    return response.data;
  }

  async createAdapter(data: CreateAdapterRequest): Promise<ImportAdapter> {
    const response = await this.client.post('/api/imports/adapters', data);
    return response.data;
  }

  async updateAdapter(id: number, data: Partial<CreateAdapterRequest>): Promise<ImportAdapter> {
    const response = await this.client.put(`/api/imports/adapters/${id}`, data);
    return response.data;
  }

  async deleteAdapter(id: number): Promise<void> {
    await this.client.delete(`/api/imports/adapters/${id}`);
  }

  async uploadSmartImport(file: File, accountId: number | null, adapterId: string): Promise<{ stagedImportId: string }> {
    const formData = new FormData();
    formData.append('file', file);
    if (accountId != null) formData.append('accountId', accountId.toString());
    formData.append('adapterId', adapterId);
    const response = await this.client.post('/api/imports/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getStagedImport(id: string, params?: { page?: number; limit?: number; status?: string; categoryId?: number; uncategorized?: boolean; heldBack?: boolean }): Promise<StagedImportResponse> {
    const response = await this.client.get(`/api/imports/${id}`, { params });
    return response.data;
  }

  async updateImportRow(importId: string, rowId: string, data: {
    suggestedCategoryId?: number;
    accountId?: number;
    status?: string;
    details?: string | null;
    ticker?: string | null;
    assetQuantity?: number | null;
    assetPrice?: number | null;
  }): Promise<StagedImportRow> {
    const response = await this.client.put(`/api/imports/${importId}/rows/${rowId}`, data);
    return response.data;
  }

  async bulkConfirmImportRows(
    id: string,
    body: { categoryId?: number; uncategorized?: boolean },
  ): Promise<{ confirmed: number }> {
    const response = await this.client.post(`/api/imports/${id}/bulk-confirm`, body);
    return response.data;
  }

  async commitImport(id: string, rowIds?: string[]): Promise<{ status: string; message: string }> {
    const response = await this.client.post(`/api/imports/${id}`, rowIds ? { rowIds } : null, { params: { action: 'commit' } });
    return response.data;
  }

  async cancelImport(id: string): Promise<void> {
    await this.client.post(`/api/imports/${id}`, null, { params: { action: 'cancel' } });
  }

  async getPendingImports(): Promise<{ imports: Array<{ id: string; fileName: string; adapterName: string | null; accountId: number | null; totalRows: number; pendingRowCount: number; createdAt: string }> }> {
    const response = await this.client.get('/api/imports/pending');
    return response.data;
  }

  // --- Plaid Transaction Review ---

  async getPlaidTransactions(params?: {
    page?: number;
    limit?: number;
    promotionStatus?: string;
    plaidItemId?: string;
    minConfidence?: number;
    maxConfidence?: number;
    categoryId?: number;
    uncategorized?: boolean;
  }): Promise<PlaidTransactionsResponse> {
    const response = await this.client.get('/api/plaid/transactions', { params });
    return response.data;
  }

  async updatePlaidTransaction(id: string, data: {
    suggestedCategoryId?: number;
    promotionStatus?: 'PROMOTED' | 'SKIPPED' | 'CLASSIFIED';
    ticker?: string;
    assetQuantity?: number;
    assetPrice?: number;
    details?: string;
  }): Promise<PlaidTransaction> {
    const response = await this.client.put(`/api/plaid/transactions/${id}`, data);
    return response.data;
  }

  async bulkPromotePlaidTransactions(params: {
    minConfidence?: number;
    plaidItemId?: string;
    categoryId?: number;
    transactionIds?: string[];
    /** When set, every transaction in the batch is assigned this category (drawer "promote-all" flow). */
    overrideCategoryId?: number;
    /** Tag names (find-or-create) applied to every newly-created transaction in the batch. */
    tags?: string[];
  }): Promise<{ promoted: number; skipped: number; errors: number; tagsApplied?: boolean }> {
    const response = await this.client.post('/api/plaid/transactions/bulk-promote', params);
    return response.data;
  }

  // --- Tenant Settings (AI classification thresholds, portfolio currency, Plaid history) ---

  async getTenantSettings(): Promise<{ autoPromoteThreshold: number; reviewThreshold: number; portfolioCurrency: string; plaidHistoryDays: number }> {
    const response = await this.client.get('/api/tenants/settings');
    return response.data;
  }

  // Aliases for backwards compatibility with useUserSettings hook
  async getUserSettings() {
    return this.getTenantSettings();
  }

  async updateTenantSettings(data: { autoPromoteThreshold?: number; reviewThreshold?: number; portfolioCurrency?: string; plaidHistoryDays?: number }): Promise<{ autoPromoteThreshold: number; reviewThreshold: number; portfolioCurrency: string; plaidHistoryDays: number }> {
    const response = await this.client.put('/api/tenants/settings', data);
    return response.data;
  }

  async updateUserSettings(data: { autoPromoteThreshold?: number; reviewThreshold?: number; portfolioCurrency?: string; plaidHistoryDays?: number }) {
    return this.updateTenantSettings(data);
  }

  // --- Ticker Search (Sprint 14) ---

  async searchTickers(query: string, type?: string): Promise<{ results: Array<{ symbol: string; name: string; exchange: string; country: string; currency: string; type: string; mic_code: string }> }> {
    const params: Record<string, string> = { q: query };
    if (type) params.type = type;
    const response = await this.client.get('/api/ticker/search', { params });
    return response.data;
  }

  // --- Sprint 12: Plaid Hardening ---

  async resyncPlaidItem(plaidItemId: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/plaid/resync', null, { params: { id: plaidItemId } });
    return response.data;
  }

  async fetchHistoricalTransactions(plaidItemId: string, fromDate: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/plaid/fetch-historical', { fromDate }, { params: { id: plaidItemId } });
    return response.data;
  }

  async rotatePlaidToken(plaidItemId: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/plaid/rotate-token', null, { params: { id: plaidItemId } });
    return response.data;
  }

  async disconnectPlaidItem(plaidItemId: string): Promise<{ message: string }> {
    const response = await this.client.post('/api/plaid/disconnect', null, { params: { id: plaidItemId } });
    return response.data;
  }

  async getPlaidSyncLogs(plaidItemId: string, limit?: number): Promise<PlaidSyncLog[]> {
    const response = await this.client.get('/api/plaid/sync-logs', { params: { plaidItemId, limit } });
    return response.data;
  }

  async getMerchantHistory(description: string, limit?: number): Promise<MerchantHistoryTransaction[]> {
    const response = await this.client.get('/api/transactions/merchant-history', { params: { description, limit } });
    return response.data;
  }

  async bulkRequeuePlaidTransactions(plaidItemId?: string): Promise<{ updated: number }> {
    const response = await this.client.post('/api/plaid/transactions/bulk-requeue', { plaidItemId });
    return response.data;
  }

  async requeuePlaidTransaction(id: string): Promise<PlaidTransaction> {
    const response = await this.client.put(`/api/plaid/transactions/${id}`, { promotionStatus: 'CLASSIFIED' });
    return response.data;
  }

  async retryPlaidTransaction(id: string): Promise<PlaidTransaction> {
    const response = await this.client.post(`/api/plaid/transactions/${id}/retry`);
    return response.data;
  }

  // --- Quick Seed Interview ---

  async getPlaidSeeds(plaidItemId: string, limit?: number): Promise<SeedItem[]> {
    const response = await this.client.get('/api/plaid/transactions/seeds', {
      params: { plaidItemId, ...(limit != null && { limit }) },
    });
    return response.data;
  }

  async confirmPlaidSeeds(
    plaidItemId: string,
    seeds: { description: string; rawName?: string; confirmedCategoryId: number }[]
  ): Promise<{ confirmed: number }> {
    const response = await this.client.post('/api/plaid/transactions/confirm-seeds', {
      plaidItemId,
      seeds,
    });
    return response.data;
  }

  async getImportSeeds(importId: string, limit?: number): Promise<SeedItem[]> {
    const response = await this.client.get(`/api/imports/${importId}/seeds`, {
      params: { ...(limit != null && { limit }) },
    });
    return response.data;
  }

  async confirmImportSeeds(
    importId: string,
    seeds: { description: string; confirmedCategoryId: number }[]
  ): Promise<{ confirmed: number }> {
    const response = await this.client.post(`/api/imports/${importId}/confirm-seeds`, { seeds });
    return response.data;
  }

  // --- Onboarding Progress ---

  async getOnboardingProgress(): Promise<{ onboardingProgress: OnboardingProgress; onboardingCompletedAt: string | null }> {
    const response = await this.client.get('/api/onboarding/progress');
    return response.data;
  }

  async completeOnboardingStep(step: string, data?: Record<string, unknown>): Promise<{ onboardingProgress: OnboardingProgress; onboardingCompletedAt: string | null }> {
    const response = await this.client.put('/api/onboarding/progress', { step, data });
    return response.data;
  }

  // --- Insights ---

  async getInsights(params?: {
    limit?: number;
    offset?: number;
    lens?: string;
    severity?: string;
    tier?: string;
    category?: string;
    periodKey?: string;
    includeDismissed?: boolean;
  }): Promise<{
    insights: Insight[];
    total: number;
    tierSummary: Record<string, { latestDate: string; latestCreatedAt: string }>;
    categoryCounts: Record<string, number>;
  }> {
    const response = await this.client.get('/api/insights', { params });
    return response.data;
  }

  async dismissInsight(insightId: string, dismissed: boolean): Promise<void> {
    const response = await this.client.put('/api/insights', { insightId, dismissed });
    return response.data;
  }

  async generateInsights(options: {
    // `tier` is required — the retired DAILY fallback was removed in v1.
    tier: 'MONTHLY' | 'QUARTERLY' | 'ANNUAL' | 'PORTFOLIO';
    year?: number;
    month?: number;
    quarter?: number;
    periodKey?: string;
    force?: boolean;
  }): Promise<{ message: string; tier: string }> {
    const response = await this.client.post('/api/insights', options);
    return response.data;
  }

  // --- Notifications ---

  async getNotificationSummary(): Promise<{ totalUnseen: number; lastSeenAt: string | null; signals: UserSignal[] }> {
    const response = await this.client.get('/api/notifications/summary');
    return response.data;
  }

  /** Processing status for the tenant (#100): what background work is in flight. */
  async getActivity(): Promise<ActivityResponse> {
    const response = await this.client.get('/api/activity');
    return response.data;
  }

  async markNotificationsSeen(): Promise<void> {
    await this.client.put('/api/notifications/summary');
  }

  // --- Equity Analysis ---

  async getEquityAnalysis(params: {
    groupBy?: string;
    /** Filter to a specific brokerage account */
    accountId?: number;
    /** Spread ETFs across their sectors / countries (#79). Server default: true. */
    lookThrough?: boolean;
  } = {}): Promise<import('@/types/equity-analysis').EquityAnalysisResponse> {
    const response = await this.client.get('/api/portfolio/equity-analysis', { params });
    return response.data;
  }

  /** Set (or clear with `null`) a holding's asset class override (#79). */
  async setAssetClass(
    portfolioItemId: number,
    assetClass: import('@/types/equity-analysis').AssetClass | null,
    { applyToSymbol = true }: { applyToSymbol?: boolean } = {},
  ): Promise<import('@/types/equity-analysis').SetAssetClassResponse> {
    const response = await this.client.put(`/api/portfolio/items/${portfolioItemId}/asset-class`, {
      assetClass,
      applyToSymbol,
    });
    return response.data;
  }

  // --- Admin Maintenance (tenant-admin only) ---

  async getRebuildStatus(): Promise<RebuildStatusResponse> {
    const response = await this.client.get('/api/admin/rebuild');
    return response.data;
  }

  async triggerRebuild(body: RebuildTriggerRequest): Promise<RebuildTriggerResponse> {
    const response = await this.client.post('/api/admin/rebuild', body);
    return response.data;
  }

  async refreshStockFundamentals(): Promise<{ message: string; jobId: string }> {
    const response = await this.client.post('/api/admin/refresh-fundamentals');
    return response.data;
  }

  // --- Subscriptions / recurring charges ---

  async getSubscriptions(params?: {
    view?: SubscriptionsView;
    categoryId?: number;
    page?: number;
    limit?: number;
  }): Promise<SubscriptionsResponse> {
    const response = await this.client.get('/api/subscriptions', { params });
    return response.data;
  }

  async confirmSubscription(body: { descriptionHash?: string; transactionId?: number }): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', { action: 'confirm', ...body });
    return response.data;
  }

  async dismissSubscription(descriptionHash: string): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', { action: 'dismiss', descriptionHash });
    return response.data;
  }

  async restoreSubscription(descriptionHash: string): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', { action: 'restore', descriptionHash });
    return response.data;
  }

  async setSubscriptionCadence(descriptionHash: string, cadence: RecurringCadence): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', { action: 'setCadence', descriptionHash, cadence });
    return response.data;
  }

  /** Give a recurring charge a custom display name (kept across detection runs). */
  async renameSubscription(descriptionHash: string, merchantLabel: string): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', {
      action: 'rename',
      descriptionHash,
      merchantLabel,
    });
    return response.data;
  }

  /** Fold the `source` merchant's row into the `target` merchant's subscription. */
  async mergeSubscription(sourceDescriptionHash: string, targetDescriptionHash: string): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', {
      action: 'merge',
      sourceDescriptionHash,
      targetDescriptionHash,
    });
    return response.data;
  }

  /** Undo a merge — the row goes back to being detected on its own. */
  async unmergeSubscription(descriptionHash: string): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', { action: 'unmerge', descriptionHash });
    return response.data;
  }

  /** Split a row by amount — one subscription per distinct recurring price, kept across scans. */
  async splitSubscription(descriptionHash: string): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', { action: 'split', descriptionHash });
    return response.data;
  }

  /** Undo a split — the merchant goes back to one row. */
  async unsplitSubscription(descriptionHash: string): Promise<unknown> {
    const response = await this.client.post('/api/subscriptions', { action: 'unsplit', descriptionHash });
    return response.data;
  }

  async refreshSubscriptions(): Promise<{ status: string; mode: string }> {
    const response = await this.client.post('/api/subscriptions', { action: 'refresh' });
    return response.data;
  }

  async fullHistoryScan(): Promise<{ status: string; mode: string }> {
    const response = await this.client.post('/api/subscriptions', { action: 'fullScan' });
    return response.data;
  }

  // --- Passive Income (#77) ---

  async getPassiveIncome(horizon: 12 | 24 | 36 = 12): Promise<PassiveIncomeResponse> {
    const response = await this.client.get('/api/portfolio/passive-income', { params: { horizon } });
    return response.data;
  }

  async getAssetIncomeTerms(assetId: number): Promise<AssetIncomeTermsResponse> {
    const response = await this.client.get(`/api/portfolio/items/${assetId}/income-terms`);
    return response.data;
  }

  async saveAssetIncomeTerms(
    assetId: number,
    body: IncomeTermsRequest,
  ): Promise<{ terms: IncomeTerms; appliedTo: number[] }> {
    const response = await this.client.put(`/api/portfolio/items/${assetId}/income-terms`, body);
    return response.data;
  }

  /** `applyToSymbol` removes the terms from every holding of the symbol (#83). */
  async deleteAssetIncomeTerms(assetId: number, opts: { applyToSymbol?: boolean } = {}): Promise<void> {
    await this.client.delete(`/api/portfolio/items/${assetId}/income-terms`, {
      params: opts.applyToSymbol ? { applyToSymbol: 'true' } : undefined,
    });
  }

  async getIncomeStreams(): Promise<IncomeStreamsResponse> {
    const response = await this.client.get('/api/passive-income/streams');
    return response.data;
  }

  async createIncomeStream(body: IncomeTermsRequest): Promise<IncomeStream> {
    const response = await this.client.post('/api/passive-income/streams', body);
    return response.data;
  }

  async updateIncomeStream(id: number, body: IncomeTermsRequest): Promise<IncomeStream> {
    const response = await this.client.put(`/api/passive-income/streams/${id}`, body);
    return response.data;
  }

  async deleteIncomeStream(id: number): Promise<void> {
    await this.client.delete(`/api/passive-income/streams/${id}`);
  }

  async getDetachedIncomeTerms(): Promise<{ detached: DetachedIncomeTerms[] }> {
    const response = await this.client.get('/api/portfolio/income-terms/detached');
    return response.data;
  }

  async attachIncomeTerms(id: number, assetId: number): Promise<{ terms: IncomeTerms }> {
    const response = await this.client.post(`/api/portfolio/income-terms/${id}/attach`, { assetId });
    return response.data;
  }

  async discardIncomeTerms(id: number): Promise<void> {
    await this.client.delete(`/api/portfolio/income-terms/${id}`);
  }

  // --- OAuth consent for the MCP server (#89) ---

  async getOAuthRequest(id: string): Promise<OAuthConsentResponse> {
    const response = await this.client.get(`/api/oauth/requests/${encodeURIComponent(id)}`);
    return response.data;
  }

  async approveOAuthRequest(
    id: string,
    body: { accessLevel: IntegrationAccessLevel; expiresInDays: KeyExpiryDays },
  ): Promise<OAuthDecisionResponse> {
    const response = await this.client.post(`/api/oauth/requests/${encodeURIComponent(id)}/approve`, body);
    return response.data;
  }

  async denyOAuthRequest(id: string): Promise<OAuthDecisionResponse> {
    const response = await this.client.post(`/api/oauth/requests/${encodeURIComponent(id)}/deny`);
    return response.data;
  }

  // --- Integrations & API tokens (#84, admin only) ---

  async getIntegrations(): Promise<{ integrations: Integration[] }> {
    const response = await this.client.get('/api/integrations');
    return response.data;
  }

  async createIntegration(body: CreateIntegrationRequest): Promise<CreateIntegrationResponse> {
    const response = await this.client.post('/api/integrations', body);
    return response.data;
  }

  async updateIntegration(id: string, body: { name?: string; description?: string | null }): Promise<{ integration: Integration }> {
    const response = await this.client.patch(`/api/integrations/${encodeURIComponent(id)}`, body);
    return response.data;
  }

  async revokeIntegration(id: string): Promise<{ integration: Integration }> {
    const response = await this.client.delete(`/api/integrations/${encodeURIComponent(id)}`);
    return response.data;
  }

  async createApiKey(integrationId: string, body: CreateApiKeyRequest): Promise<CreateApiKeyResponse> {
    const response = await this.client.post(`/api/integrations/${encodeURIComponent(integrationId)}/keys`, body);
    return response.data;
  }

  async revokeApiKey(integrationId: string, keyId: string): Promise<{ apiKey: ApiKeySummary }> {
    const response = await this.client.delete(
      `/api/integrations/${encodeURIComponent(integrationId)}/keys/${encodeURIComponent(keyId)}`,
    );
    return response.data;
  }
}

export const api = new APIClient();
export default api; 